import React, { useState } from "react";
import {
  Box,
  BoxProps,
  Button,
  LinkButton,
  useTheme,
  ButtonProps,
} from "@catchoftheday/cg-components";
import axios, { AxiosResponse } from "axios";
import useSWR from "swr";
import { AssetType } from "@search-app/types";
import {
  CONSTANTS,
  HeaderMetaData,
  HeaderMiniCart,
  MiniCartItem,
} from "@catchoftheday/header-app";
import {
  triggerCCFreeShippingDrawer,
  triggerCCExclusiveModal,
} from "@catchoftheday/common-vendors";
import { addToCart } from "./utils";

const fetcher = axios.create();

type AddToCartProps = {
  attributeName?: string;
  eventId: number;
  id: number;
  images: AssetType[];
  isPersonalisedProduct?: boolean;
  offerId?: number;
  quantityForSale: number;
  sellerId?: number;
  sellPrice: number;
  sourceId?: string | number;
  sourcePosition?: number;
  sourceType?: number;
  sourcePlatform?: number | null;
  title: string;
  trackEventToken?: string;
  url: string;
  clubCatchExclusive?: boolean;
};

export const AddToCart = ({
  attributeName,
  eventId,
  id,
  isPersonalisedProduct,
  offerId,
  quantityForSale,
  sellerId,
  sellPrice,
  sourceId,
  sourcePosition,
  sourceType,
  sourcePlatform,
  title,
  trackEventToken,
  url,
  images,
  clubCatchExclusive,
}: AddToCartProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const { data: headerMetaData } = useSWR<AxiosResponse<HeaderMetaData>>(
    CONSTANTS.HEADER_META_DATA_API,
    {
      fetcher,
    }
  );

  const { data: headerMiniCart } = useSWR<AxiosResponse<HeaderMiniCart>>(
    CONSTANTS.HEADER_MINICART_API,
    {
      fetcher,
    }
  );

  async function fetchMiniCart(): Promise<HeaderMiniCart> {
    const { data } = await axios.get(CONSTANTS.HEADER_MINICART_API);
    return data;
  }

  function triggerFreeShippingDrawer(
    totalPrice: number,
    items: MiniCartItem[],
    freeShippingClubCatch: boolean,
    isAdult: boolean
  ) {
    if (
      sellPrice <= 75 &&
      totalPrice <= 75 &&
      freeShippingClubCatch &&
      headerMetaData?.data.clubActive
    ) {
      triggerCCFreeShippingDrawer({
        productId: id,
        sellerId,
        cartItems: items,
        isAdult,
      });
    }
  }

  if (!quantityForSale) {
    return null;
  }

  if (attributeName) {
    const text = `Select ${attributeName}`;
    return (
      <AddToCartContainer>
        <AddToCartLink link={url} text={text} productId={id} />
      </AddToCartContainer>
    );
  }

  if (isPersonalisedProduct) {
    return (
      <AddToCartContainer>
        <AddToCartLink link={url} text="Choose Options" productId={id} />
      </AddToCartContainer>
    );
  }

  if (!offerId || !sellerId) {
    return (
      <AddToCartContainer>
        <AddToCartLink link={url} text="View Product" productId={id} />
      </AddToCartContainer>
    );
  }

  const productImages = images.find((image) => image.type === "product");
  const productThumbnail = productImages?.sources.find(
    (source) => source.descriptor === "200w"
  )?.src;

  const shouldTriggerCCExModal =
    !headerMetaData?.data.clubActive &&
    clubCatchExclusive &&
    !headerMiniCart?.data.containsClubCatchLineItem;

  function triggerCCExModal() {
    if (shouldTriggerCCExModal && offerId && sellerId) {
      triggerCCExclusiveModal({
        price: sellPrice,
        title,
        imageUrl: productThumbnail || "",
        eventId,
        offerId,
        sellerId,
        id,
        isLoggedIn: headerMetaData?.data.loggedIn,
      });
    }
  }

  const ctaText =
    !clubCatchExclusive ||
    (clubCatchExclusive && headerMetaData?.data.clubActive) ||
    (clubCatchExclusive && headerMiniCart?.data.containsClubCatchLineItem)
      ? "Add to Cart"
      : "Join to Buy";

  return (
    <AddToCartContainer>
      <Box className="button-controller">
        <AddToCartButton
          text={ctaText}
          disabled={isLoading}
          onClick={async (e) => {
            e.stopPropagation();
            setIsLoading(true);

            if (shouldTriggerCCExModal) {
              triggerCCExModal();
              setIsLoading(false);
              return;
            }

            const result: {
              has_club_catch_free_shipping_alert: boolean;
              is_adult_category: boolean;
            } = await addToCart({
              sourceId,
              sourceType,
              sourcePosition,
              sourcePlatform,
              eventId,
              offerId,
              sellerId,
              id,
              trackEventToken,
              imageUrl: productThumbnail || "",
              title,
            }).finally(() => setIsLoading(false));

            const { totalPrice, items } = await fetchMiniCart();
            if (result?.has_club_catch_free_shipping_alert) {
              triggerFreeShippingDrawer(
                totalPrice,
                items,
                result?.has_club_catch_free_shipping_alert,
                result?.is_adult_category
              );
            }
          }}
        />
      </Box>
    </AddToCartContainer>
  );
};

const AddToCartContainer = (props: BoxProps) => (
  <Box padding="10px" className="product--buy-form--container" {...props} />
);

type AddToCartLinkProps = {
  link?: string;
  productId: number;
  text: string;
};

const AddToCartLink = ({ link, text, productId }: AddToCartLinkProps) => (
  <LinkButton
    block
    sizeVariant="sm"
    href={link}
    title={text}
    data-product-id={productId}
    variant="secondary"
    outline
    fontSize="xs"
  >
    {text}
  </LinkButton>
);

type AddToCartButtonProps = {
  text: string;
} & ButtonProps;

const AddToCartButton = ({ text, ...props }: AddToCartButtonProps) => {
  const theme = useTheme();

  return (
    <Button
      fontFamily={theme.fontFamily}
      fontSize="xs"
      block
      sizeVariant="sm"
      title={text}
      type="submit"
      {...props}
    >
      {text}
    </Button>
  );
};
